import { defineStore } from 'pinia';
import axios from 'axios';
import { dashboardStatsService } from '@/services/dashboardStatsService';

/**
 * The list of widgets covered by the combined `/dashboard/stats`
 * endpoint. Order doesn't matter for the API — it's used for the
 * "refresh all per-widget" loops.
 */
const COMBINED_WIDGETS = [
  'kpis',
  'funnel',
  'revenueTimeline',
  'pipeline',
  'health',
  'leaderboard',
  'installerStats',
  'products',
];

/**
 * Margin widgets (added 2026-05-12). These are deliberately *not*
 * included in the combined endpoint — coverage is initially low and
 * the queries are heavier, so the backend serves them on dedicated
 * endpoints. The store fans them out in parallel with the combined
 * call so first-paint still only takes one network wait.
 */
const MARGIN_WIDGETS = ['marginTotals', 'marginProducts', 'marginUnderThreshold'];

/**
 * Forecast widget (Phase 3 — 2026-05-12). Driven by a dedicated `year`
 * filter rather than the dashboard-wide period — so we keep it in its
 * own bucket and don't loop it through the period-aware refresh paths.
 */
const FORECAST_WIDGETS = ['forecast'];

/**
 * Rejection-reason ranking. Dedicated endpoint so the combined
 * payload stays unchanged — same fan-out pattern as margin.
 */
const REJECT_REASON_WIDGETS = ['rejectReasons'];

/**
 * Pipeline is the only combined widget that ignores the `period`
 * filter — it always reflects the current open pipeline.
 */
const PIPELINE_WIDGETS = ['pipeline'];

const WIDGETS = [
  ...COMBINED_WIDGETS,
  ...MARGIN_WIDGETS,
  ...FORECAST_WIDGETS,
  ...REJECT_REASON_WIDGETS,
];

/**
 * Map a widget key to its service method (used for per-widget
 * refresh and for fan-out of the margin sub-endpoints).
 */
const WIDGET_FETCHERS = {
  kpis: 'getKpis',
  funnel: 'getFunnel',
  revenueTimeline: 'getRevenueTimeline',
  pipeline: 'getPipeline',
  health: 'getHealth',
  leaderboard: 'getLeaderboard',
  installerStats: 'getInstallerStats',
  products: 'getProducts',
  marginTotals: 'getMarginTotals',
  marginProducts: 'getMarginProducts',
  marginUnderThreshold: 'getMarginUnderThreshold',
  rejectReasons: 'getRejectReasons',
  // Forecast uses a dedicated `year` filter (see `fetchForecast`).
  // We don't list it in WIDGET_FETCHERS because its argument shape
  // differs from the rest — it would break `fetchWidget`'s generic
  // path. Refresh it via `fetchForecast` instead.
};

/**
 * The combined-endpoint response uses snake_case section keys. This
 * map normalises them to the camelCase keys we use in the store so
 * components don't have to deal with both spellings.
 */
const COMBINED_TO_STORE_KEY = {
  kpis: 'kpis',
  funnel: 'funnel',
  revenue_timeline: 'revenueTimeline',
  pipeline: 'pipeline',
  health: 'health',
  leaderboard: 'leaderboard',
  installer_stats: 'installerStats',
  products: 'products',
};

const emptyWidgetState = () => ({
  // The unwrapped `data` payload (sold/installed/collected for kpis,
  // steps/conversion for funnel, etc). Both the combined endpoint and
  // the sub-endpoint nest the actual values under a `data` key — we
  // unwrap once in the store so components never have to write
  // `widget.data?.data` and stay readable.
  data: null,
  // Window the data describes (mirrors backend `period_start` /
  // `period_end`). For pipeline it stays null because the API ignores
  // period there.
  periodStart: null,
  periodEnd: null,
  loading: false,
  error: null,
  // Capture the timestamp of the last successful fetch so the UI can
  // show "updated 30s ago" type hints without re-fetching.
  fetchedAt: null,
});

/**
 * Normalise a widget payload to `{ data, periodStart, periodEnd }`.
 *
 * The combined `/dashboard/stats` endpoint inlines each section's
 * payload directly (e.g. `kpis: { sold, installed, ..., counts }`),
 * while the per-widget sub-endpoints wrap it in an envelope
 * (`{ period, period_start, period_end, data: {...} }`). We need to
 * support both shapes — detect the envelope by the presence of a
 * `data` property and unwrap when present, otherwise treat the section
 * itself as the data.
 */
const unwrap = (section) => {
  if (!section || typeof section !== 'object') {
    return { data: null, periodStart: null, periodEnd: null };
  }
  // Sub-endpoint envelope: { period, period_start, period_end, data }
  if (section.data && typeof section.data === 'object') {
    return {
      data: section.data,
      periodStart: section.period_start ?? null,
      periodEnd: section.period_end ?? null,
    };
  }
  // Combined-endpoint section: the payload itself.
  return {
    data: section,
    periodStart: section.period_start ?? null,
    periodEnd: section.period_end ?? null,
  };
};

const initialWidgetMap = () =>
  WIDGETS.reduce((acc, key) => {
    acc[key] = emptyWidgetState();
    return acc;
  }, {});

const isCanceledError = (error) => {
  if (!error) return false;
  // Axios v1 uses ERR_CANCELED, v0 uses error.message === 'canceled',
  // and AbortController signals throw `CanceledError` from axios.
  if (axios.isCancel && axios.isCancel(error)) return true;
  if (error.code === 'ERR_CANCELED') return true;
  if (error.name === 'CanceledError' || error.name === 'AbortError') return true;
  return false;
};

export const useDashboardStatsStore = defineStore('dashboardStats', {
  state: () => ({
    // ============================
    // Filters (drive every fetch)
    // ============================
    filters: {
      period: '30d',
      dateFrom: null,
      dateTo: null,
      scope: 'organization',
      pipelineId: null,
      userId: null,
      installerUserId: null,
    },

    // ============================
    // Forecast widget filter
    //
    // The forecast widget supports two modes (2026-05-13 delta):
    //   - 'year'   → drives the API via `year`. Standalone — the
    //                year switcher in the widget header chooses it.
    //                This is the default to preserve the original
    //                "annual target vs sold" view.
    //   - 'window' → drives the API via `period` (+ custom dates).
    //                Follows the dashboard's main period filter so
    //                every widget on the page covers the same range.
    // `forecastYear` is only used in year-mode; `filters.period` /
    // `filters.dateFrom` / `filters.dateTo` are reused in window-mode.
    // ============================
    forecastMode: 'year',
    forecastYear: null,

    // ============================
    // Combined-endpoint window
    // (returned by /stats — informs every widget that doesn't have
    //  its own period_start/period_end)
    // ============================
    periodStart: null,
    periodEnd: null,

    // ============================
    // Per-widget state
    // ============================
    widgets: initialWidgetMap(),

    // Top-level "first paint" loading flag. True until the very first
    // combined fetch has resolved — used to render the page-level
    // skeleton instead of per-widget skeletons.
    initialLoading: false,
    initialLoaded: false,

    // ============================
    // Cancellation tokens
    // ============================
    // We keep one AbortController for the combined fetch and one per
    // widget so a filter change cancels in-flight requests cleanly.
    _combinedController: null,
    _widgetControllers: {},
    _forecastController: null,
  }),

  getters: {
    /**
     * Convenience getter for the combined window — used by the page
     * header to render "12 apr — 12 mei" subtitle.
     */
    periodWindow(state) {
      if (!state.periodStart || !state.periodEnd) return null;
      return { start: state.periodStart, end: state.periodEnd };
    },

    /**
     * True if any widget is currently loading. Useful for the "refresh"
     * button spinner state.
     */
    anyLoading(state) {
      return WIDGETS.some((w) => state.widgets[w]?.loading);
    },

    /**
     * `true` once we have data for every widget (or an error per
     * widget — empty data is also a success).
     */
    allLoaded(state) {
      return WIDGETS.every(
        (w) => state.widgets[w]?.data !== null || state.widgets[w]?.error !== null
      );
    },

    /**
     * The window-aware widgets that *do* care about period changes.
     * Pipeline is excluded because the API ignores period there.
     */
    periodAwareWidgetKeys: () => WIDGETS.filter((w) => !PIPELINE_WIDGETS.includes(w)),
  },

  actions: {
    // ============================================================
    // Filter mutations
    // ============================================================

    /**
     * Replace one or more filters and re-fetch the dashboard. Callers
     * can pass any subset of {period, dateFrom, dateTo, scope,
     * pipelineId, userId, installerUserId}.
     */
    async setFilters(patch = {}) {
      Object.assign(this.filters, patch);
      await this.fetchAll({ force: true });
    },

    setPeriod(period, { dateFrom = null, dateTo = null } = {}) {
      this.filters.period = period;
      this.filters.dateFrom = dateFrom;
      this.filters.dateTo = dateTo;
      return this.fetchAll({ force: true });
    },

    setPipelineId(pipelineId) {
      this.filters.pipelineId = pipelineId || null;
      return this.fetchAll({ force: true });
    },

    setScope(scope) {
      this.filters.scope = scope;
      return this.fetchAll({ force: true });
    },

    setUserId(userId) {
      this.filters.userId = userId || null;
      // user_id only affects /leaderboard; refresh just that widget.
      return this.fetchWidget('leaderboard');
    },

    setInstallerUserId(installerUserId) {
      this.filters.installerUserId = installerUserId || null;
      return this.fetchWidget('installerStats');
    },

    // ============================================================
    // Combined fetch (page load + filter changes)
    // ============================================================

    /**
     * Fetch the combined endpoint and fan the response out to per-
     * widget state. The margin widgets aren't part of `/dashboard/stats`,
     * so we kick off dedicated requests in parallel (Promise.all) —
     * one round-trip's worth of latency, three more independent
     * sub-fetches.
     *
     * If `force` is false and we already have data, we skip — this
     * is the call-site default for `onMounted` on the page so a
     * route revisit doesn't trigger a flash of skeletons.
     */
    async fetchAll({ force = false } = {}) {
      if (!force && this.initialLoaded) return;

      // Cancel any prior combined fetch — filter changes happen fast
      // (dropdown spam) and we don't want stale responses to win.
      if (this._combinedController) {
        this._combinedController.abort();
      }
      const controller = new AbortController();
      this._combinedController = controller;

      // Mark every widget as loading (they'll all be replaced together).
      WIDGETS.forEach((key) => {
        const widget = this.widgets[key];
        widget.loading = true;
        widget.error = null;
      });
      if (!this.initialLoaded) this.initialLoading = true;

      const combinedPromise = (async () => {
        try {
          const response = await dashboardStatsService.getCombined(this.filters, {
            signal: controller.signal,
          });

          this.periodStart = response.period_start || null;
          this.periodEnd = response.period_end || null;

          Object.entries(COMBINED_TO_STORE_KEY).forEach(([apiKey, storeKey]) => {
            const section = response[apiKey];
            const widget = this.widgets[storeKey];
            if (!widget) return;
            if (section && section.error) {
              // Per-widget failure surfaced by the combined endpoint.
              widget.data = null;
              widget.periodStart = null;
              widget.periodEnd = null;
              widget.error = {
                code: section.error,
                message: section.message || null,
              };
            } else {
              const unwrapped = unwrap(section);
              widget.data = unwrapped.data;
              widget.periodStart = unwrapped.periodStart;
              widget.periodEnd = unwrapped.periodEnd;
              widget.error = null;
              widget.fetchedAt = Date.now();
            }
            widget.loading = false;
          });
        } catch (error) {
          if (isCanceledError(error)) return;
          COMBINED_WIDGETS.forEach((key) => {
            const widget = this.widgets[key];
            widget.loading = false;
            // Don't blow away existing data on transient failure —
            // keep SWR-style rendering.
            if (!widget.data) {
              widget.error = {
                code: 'failed',
                message: error?.response?.data?.error || error?.message || 'failed',
              };
            }
          });
          throw error;
        }
      })();

      // Fan-out the margin sub-endpoints. They're independent of the
      // combined response and resolve on their own pace — we don't
      // want a slow margin query to delay the rest of the dashboard.
      const marginPromises = MARGIN_WIDGETS.map((key) => this.fetchWidget(key));
      const rejectReasonPromises = REJECT_REASON_WIDGETS.map((key) =>
        this.fetchWidget(key)
      );

      // Forecast is also independent. We trigger it here so first-paint
      // includes the chart, but a slow query doesn't block the page.
      const forecastPromise = this.fetchForecast();

      try {
        // Wait for the combined fetch (it's the page's blocking call).
        // Extra fan-out resolves independently; we still await them so
        // a caller-level `await fetchAll()` reflects "everything is
        // settled", but errors there don't take the whole dashboard down.
        await combinedPromise;
        await Promise.allSettled([
          ...marginPromises,
          ...rejectReasonPromises,
          forecastPromise,
        ]);
        this.initialLoaded = true;
      } finally {
        if (this._combinedController === controller) {
          this._combinedController = null;
        }
        this.initialLoading = false;
      }
    },

    // ============================================================
    // Forecast widget (Phase 3)
    // ============================================================

    /**
     * Change which year the forecast widget displays and refetch.
     * Pass `null` to fall back to "current year" on the next fetch.
     * Implicitly puts the widget in year-mode (the year switcher only
     * exists in that mode).
     */
    setForecastYear(year) {
      this.forecastYear = year == null ? null : Number(year);
      this.forecastMode = 'year';
      return this.fetchForecast();
    },

    /**
     * Switch the forecast widget between year-mode and window-mode.
     * Window-mode reuses the dashboard's main period filter, so no
     * extra args are needed — `fetchForecast` reads it directly.
     */
    setForecastMode(mode) {
      if (mode !== 'year' && mode !== 'window') return;
      if (this.forecastMode === mode) return;
      this.forecastMode = mode;
      return this.fetchForecast();
    },

    /**
     * Fetch the forecast. Behaviour depends on `forecastMode`:
     *
     *   - 'year'   → uses `year` (defaults to calendar year). The
     *                backend echoes the resolved year; we sync
     *                `forecastYear` from the response.
     *   - 'window' → uses the dashboard's `filters.period` (+ optional
     *                `dateFrom`/`dateTo` for `period === 'custom'`).
     *
     * Explicit `year` arg still works and forces year-mode for that
     * single call — used by `setForecastYear`.
     */
    async fetchForecast({ year } = {}) {
      const widget = this.widgets.forecast;
      if (!widget) return;

      // Explicit `year` overrides current mode for this single fetch.
      const useWindow = year == null && this.forecastMode === 'window';

      if (this._forecastController) this._forecastController.abort();
      const controller = new AbortController();
      this._forecastController = controller;

      widget.loading = true;
      widget.error = null;

      let params;
      if (useWindow) {
        params = { period: this.filters.period };
        if (this.filters.period === 'custom') {
          // Don't fire if the custom window is incomplete — the
          // backend rejects it with 400 and a partial range would
          // give misleading buckets anyway.
          if (!this.filters.dateFrom || !this.filters.dateTo) {
            widget.loading = false;
            this._forecastController = null;
            return;
          }
          params.dateFrom = this.filters.dateFrom;
          params.dateTo = this.filters.dateTo;
        }
      } else {
        const targetYear = year ?? this.forecastYear ?? new Date().getFullYear();
        params = { year: targetYear };
      }

      try {
        const response = await dashboardStatsService.getForecast(params, {
          signal: controller.signal,
        });
        const unwrapped = unwrap(response);
        widget.data = unwrapped.data;
        widget.periodStart = unwrapped.periodStart || unwrapped.data?.period_start || null;
        widget.periodEnd = unwrapped.periodEnd || unwrapped.data?.period_end || null;
        widget.fetchedAt = Date.now();

        // Year-mode bookkeeping: lock in the resolved year so the
        // year-switcher stays in sync with what's rendered.
        if (!useWindow) {
          if (unwrapped.data?.year != null) {
            this.forecastYear = unwrapped.data.year;
          } else {
            this.forecastYear = params.year;
          }
        }
      } catch (error) {
        if (isCanceledError(error)) return;
        widget.error = {
          code: 'failed',
          message: error?.response?.data?.error || error?.message || 'failed',
        };
      } finally {
        widget.loading = false;
        if (this._forecastController === controller) {
          this._forecastController = null;
        }
      }
    },

    // ============================================================
    // Per-widget refresh
    // ============================================================

    /**
     * Refresh a single widget via its sub-endpoint. Used by the
     * per-widget refresh button and by user_id / installer_user_id
     * filter changes that only affect one widget.
     */
    async fetchWidget(key) {
      const widget = this.widgets[key];
      const fetcher = WIDGET_FETCHERS[key];
      if (!widget || !fetcher) return;

      // Cancel previous in-flight fetch for this widget.
      const existing = this._widgetControllers[key];
      if (existing) existing.abort();
      const controller = new AbortController();
      this._widgetControllers[key] = controller;

      widget.loading = true;
      widget.error = null;

      try {
        const response = await dashboardStatsService[fetcher](this.filters, {
          signal: controller.signal,
        });
        const unwrapped = unwrap(response);
        widget.data = unwrapped.data;
        widget.periodStart = unwrapped.periodStart;
        widget.periodEnd = unwrapped.periodEnd;
        widget.fetchedAt = Date.now();
      } catch (error) {
        if (isCanceledError(error)) return;
        widget.error = {
          code: 'failed',
          message: error?.response?.data?.error || error?.message || 'failed',
        };
      } finally {
        widget.loading = false;
        if (this._widgetControllers[key] === controller) {
          delete this._widgetControllers[key];
        }
      }
    },

    /**
     * Refresh every widget via per-widget endpoints. Heavier than
     * `fetchAll` (more network round-trips) so this is reserved for
     * "manual refresh" / polling scenarios where we want fresh
     * per-widget timestamps.
     */
    async refreshAllPerWidget() {
      await Promise.all(WIDGETS.map((key) => this.fetchWidget(key)));
    },

    /**
     * Reset the entire store — used when the user logs out / switches
     * organisation so we don't leak data across tenants.
     */
    reset() {
      // Cancel anything in flight.
      if (this._combinedController) this._combinedController.abort();
      if (this._forecastController) this._forecastController.abort();
      Object.values(this._widgetControllers).forEach((c) => c?.abort?.());
      this._combinedController = null;
      this._forecastController = null;
      this._widgetControllers = {};

      this.filters = {
        period: '30d',
        dateFrom: null,
        dateTo: null,
        scope: 'organization',
        pipelineId: null,
        userId: null,
        installerUserId: null,
      };
      this.forecastYear = null;
      this.forecastMode = 'year';
      this.periodStart = null;
      this.periodEnd = null;
      this.widgets = initialWidgetMap();
      this.initialLoading = false;
      this.initialLoaded = false;
    },
  },
});

export default useDashboardStatsStore;
